import { useCallback, useMemo } from 'react';
import { isDerivedShape } from '@larki/mp/lib/shape';
import { MapShape } from '@larki/mp/doc';
import { CoveragePatch } from '@larki/mp/lib/coverage';

import { useGeoJson2Svg } from '../../hooks/useGeojson2Svg';
import {
  useGetCoverageLabelsMutation,
  useGetCoveragePatchesMutation,
} from '../../redux/rtk/engine';
import { captureError } from '../../utilities/error';
import { colorLib } from '../lib/color';
import { arrayLib } from '../lib/array';
import { shapeLib } from '../lib/shape';
const { extractShapeGeometry, shapeContains, shapeIntersect, shapeUnion } =
  shapeLib;
import { toGeoJson2SvgPolygonFeature } from '../../utilities/geojson2svg';
import { DATA_TYPE_COLORS, resolveShapeColor } from '../constants/color';
import { downloadFile } from '../../utilities/files';

function convertToInput(shape: MapShape) {
  const shapeGeom = extractShapeGeometry(shape);
  if (!shapeGeom) {
    captureError(new Error('failed to extract shape geometry'));
    return null;
  }
  return {
    type: 'Feature' as const,
    geometry: shapeGeom,
    properties: {
      id: shape.id,
      category_name: shape.dataType,
      base_shape_id: isDerivedShape(shape) ? shape.baseShapeId : null,
      hidden: shape.hidden,
    },
  };
}

/**
 * Generate a screenshot of one or more shapes using the LARKI GeoJSON->SVG backbone.
 */
export function useShapeScrenshot() {
  const [getCoveragePatches, getCoveragePatchesState] =
    useGetCoveragePatchesMutation();
  const [getCoverageLabels, getCoverageLabelsState] =
    useGetCoverageLabelsMutation();
  const geoJson2Svg = useGeoJson2Svg();

  const isShapeScreenshotLoading =
    getCoveragePatchesState.isLoading ||
    getCoverageLabelsState.isLoading ||
    geoJson2Svg.loading;

  const generateShapeScreenshot = useCallback(
    async (targetShapes: MapShape[]) => {
      const baseShapes: MapShape[] = [];

      for (const shape of targetShapes) {
        if (isDerivedShape(shape)) {
          const baseShape = targetShapes.find(
            (s) => s.id === shape.baseShapeId
          );
          if (!baseShape) {
            captureError(new Error('failed to find base shape'));
            continue;
          }
          baseShapes.push(baseShape);
        }
      }

      const targetShapeInputs = targetShapes
        .map(convertToInput)
        .filter(arrayLib.filterNonNull);
      const baseShapeInputs = baseShapes
        .map(convertToInput)
        .filter(arrayLib.filterNonNull);

      const [coverage, { labels }] = await Promise.all([
        getCoveragePatches({
          shapes: [...targetShapeInputs, ...baseShapeInputs],
          ignore_patches: [],
        }).unwrap(),
        getCoverageLabels({
          shapes: targetShapeInputs,
          min_area: 50,
        }).unwrap(),
      ]);

      // mask patches by the shape
      const targetShapeUnion = shapeUnion(...targetShapes);
      if (!targetShapeUnion) {
        captureError(new Error('failed to union target shapes'));
        return null;
      }
      const maskedPatches: CoveragePatch[] = [];
      for (const patch of coverage.patches) {
        const geometry = shapeIntersect(patch.geometry, targetShapeUnion);
        if (!geometry) continue;
        maskedPatches.push({ ...patch, geometry });
      }

      // pre-compute color overrides: shape id -> hex, dataType -> hex
      const shapeColorById = new Map<string, string>();
      const colorByDataType = new Map<string, string>();
      for (const shape of targetShapes) {
        const hex = colorLib.rgbToHex(resolveShapeColor(shape));
        shapeColorById.set(shape.id, hex);
        if (shape.color) {
          colorByDataType.set(shape.dataType, hex);
        }
      }

      function resolveDisplayColor(categoryName: string): string {
        return (
          colorByDataType.get(categoryName) ??
          DATA_TYPE_COLORS[categoryName as keyof typeof DATA_TYPE_COLORS] ??
          DATA_TYPE_COLORS.unknown
        );
      }

      // filter labels, flat map
      const maskedLabels = labels.flatMap((label) => {
        const displayColor = resolveDisplayColor(label.category_name);
        return label.positions
          .filter((position) => shapeContains(targetShapeUnion, position))
          .map((position) => ({
            latLng: position,
            label: label.label,
            displayColor,
          }));
      });

      // reformat to geojson
      const geojson = [
        // shapes first
        ...targetShapeInputs.map((input) => {
          const category = input.properties.category_name;
          const shapeHexColor = shapeColorById.get(input.properties.id);
          const hasCustomColor = colorByDataType.has(category);
          const useShapeColor =
            (category === 'custom' || category === 'unknown') && hasCustomColor;

          return toGeoJson2SvgPolygonFeature(input.geometry, {
            ...(category === 'unknown' &&
              !hasCustomColor && {
                // ignore fill - that comes with coverage
                fillOpacity: 0,
              }),
            ...(useShapeColor && {
              fillColor: shapeHexColor,
              fillOpacity: 0.4,
              strokeColor: shapeHexColor,
              strokeOpacity: 1,
              strokeWidth: 2,
            }),
          });
        }),
        // then coverage on top
        ...maskedPatches.map((patch) => {
          const color = resolveDisplayColor(patch.category_name);
          const hasCustomColor = colorByDataType.has(patch.category_name);
          return toGeoJson2SvgPolygonFeature(patch.geometry, {
            fillColor: color,
            strokeColor: color,
            strokeOpacity: 1,
            strokeWidth: 2,
            fillOpacity:
              hasCustomColor ? 0 : patch.category_name === 'unknown' ? 0.4 : 0.6,
          });
        }),
      ];

      // get the SVG
      const svgUrl = await geoJson2Svg.generate(
        geojson,
        {
          width: 1280,
          height: 720,
        },
        maskedLabels
      );

      return svgUrl;
    },
    [geoJson2Svg, getCoverageLabels, getCoveragePatches]
  );

  const downloadShapeScreenshot = useCallback(
    async (targetShapes: MapShape[]) => {
      const svgUrl = await generateShapeScreenshot(targetShapes);
      if (!svgUrl) return;
      // DOWNLOAD!
      // @ts-expect-error
      downloadFile(svgUrl, `shape.jpg`);
    },
    [generateShapeScreenshot]
  );

  return useMemo(
    () => ({
      loading: isShapeScreenshotLoading,
      generate: generateShapeScreenshot,
      download: downloadShapeScreenshot,
    }),
    [downloadShapeScreenshot, generateShapeScreenshot, isShapeScreenshotLoading]
  );
}
